import { Check, Copy, Eye, EyeOff } from "lucide-react";
import { memo, useCallback, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export interface ICopyableSecretFieldProps {
  label: string;
  value: string;
  copyLabel: string;
  onCopy: (value: string) => void;
  isCopied?: boolean;
  isMaskable?: boolean;
  showLabel?: string;
  hideLabel?: string;
  isMonospace?: boolean;
  testId?: string;
  copyTestId?: string;
  toggleTestId?: string;
}

// NOTE: Read-only value + copy button, optionally masked behind a reveal toggle
// (NSAI-5027). Shared by the meeting's public-share section and the Shared
// Meetings tab so a link row and a password row behave identically. Presentation
// only — the parent owns the clipboard write and the "copied" flag.
const MASK_CHARACTER = "•";
const MASK_LENGTH = 12;

const CopyableSecretFieldComponent = ({
  label,
  value,
  copyLabel,
  onCopy,
  isCopied = false,
  isMaskable = false,
  showLabel,
  hideLabel,
  isMonospace = false,
  testId,
  copyTestId,
  toggleTestId,
}: ICopyableSecretFieldProps): JSX.Element => {
  const [isRevealed, setIsRevealed] = useState(false);

  const handleToggleReveal = useCallback(() => {
    setIsRevealed((previous) => !previous);
  }, []);

  const handleCopy = useCallback(() => {
    onCopy(value);
  }, [onCopy, value]);

  // NOTE: A fixed-length mask rather than one dot per character — the rendered
  // width should not leak how long the password is.
  const isHidden = isMaskable && !isRevealed;
  const displayedValue = isHidden ? MASK_CHARACTER.repeat(MASK_LENGTH) : value;

  return (
    <div>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <div className="mt-1 flex items-center gap-2">
        <Input
          readOnly
          value={displayedValue}
          data-testid={testId}
          onFocus={(event) => event.target.select()}
          className={cn("flex-1 text-xs", isMonospace && "font-mono")}
        />
        {isMaskable ? (
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label={isRevealed ? hideLabel : showLabel}
            data-testid={toggleTestId}
            onClick={handleToggleReveal}
          >
            {isRevealed ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </Button>
        ) : null}
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label={copyLabel}
          data-testid={copyTestId}
          onClick={handleCopy}
        >
          {isCopied ? (
            <Check className="h-4 w-4" />
          ) : (
            <Copy className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  );
};

export const CopyableSecretField = memo(CopyableSecretFieldComponent);
